import isNil from "lodash/isNil";
import { isEditMode } from "PFApp/booking/components/booking_form/booking_form.utils";
import { BookingType } from "PFApp/booking/components/booking_form/booking_type";
import { OnSuccessData } from "PFApp/booking/components/booking_form/use_handle_submit/use_handle_submit";
import { usePlaceholderBooking } from "PFApp/booking/components/booking_placeholder/hooks/use_placeholder_booking";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { LoadingDots } from "PFComponents/loading_dots";
import SidePanel from "PFComponents/side_panel/side_panel";
import { useBookingInvalidate } from "PFCore/hooks/queries";
import { Booking } from "PFTypes";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { DetailsSidePanelProps } from "../../../../components/details_panel/details_panel_item";
import BookingActivityContextProvider from "../../../providers/booking_activity_context_provider";
import { BookingDetailContent } from "./booking_detail_content";
import { BookingDetailHeader } from "./booking_detail_header";
import {
  BookingDetailSidePanelContextProvider,
  useBookingDetailSidePanelContext
} from "./booking_detail_side_panel_context";
import Buttons from "./buttons";

const BookingDetailSidePanelContent = ({
  isOnTop,
  data,
  show,
  onClose,
  zIndex,
  isClosing,
  disableEntranceAnimation = false
}: DetailsSidePanelProps) => {
  const { onDeleted, onEdited } = data.bookingDetails ?? {};
  const { goToDate, checkCanGoToDate } = data.bookingModuleDetails ?? {};
  const { showGrowl } = useGrowlStoreActions();
  const { t } = useTranslation("bookingModule");
  const {
    profile,
    isProfileError,
    isInitialBookingLoading,
    isSiblingBookingsFetching,
    siblingBookings,
    activeBookingIndex,
    invalidateBookingSiblings
  } = useBookingDetailSidePanelContext();
  const currentSiblingBooking = !isNil(activeBookingIndex)
    ? siblingBookings?.[activeBookingIndex]
    : undefined;
  const { invalidate: invalidateBooking } = useBookingInvalidate();
  const { checkBookingRequiresApproval } = usePlaceholderBooking();

  const canSeeActionButtons =
    !checkBookingRequiresApproval(currentSiblingBooking) &&
    (isNil(data.isEditPermitted) || data.isEditPermitted);

  const handleEdit = async ({ mode, bookingType }: OnSuccessData) => {
    if (isEditMode(mode) && bookingType === BookingType.Repeated) {
      onClose();
    }
    await invalidateBooking(currentSiblingBooking?.id);
    invalidateBookingSiblings();
    onEdited && onEdited();
  };

  const handleDelete = async (booking: Booking) => {
    invalidateBookingSiblings();
    if (onDeleted) {
      await onDeleted(booking);
    }
  };

  const title =
    !currentSiblingBooking || isInitialBookingLoading || isSiblingBookingsFetching ? (
      <LoadingDots />
    ) : (
      <BookingDetailHeader title={currentSiblingBooking?.title} />
    );

  useEffect(() => {
    if (isProfileError) {
      showGrowl({
        message: t("details.fetchingProfileError"),
        kind: "error"
      });
      onClose();
    }
  }, [isProfileError, onClose, showGrowl, t]);

  return (
    <BookingActivityContextProvider id={currentSiblingBooking?.activity_id ?? undefined}>
      <SidePanel
        qaId="BookingDetailSidePanel"
        show={show}
        zIndex={zIndex}
        isSidePanelClosing={isClosing}
        onClose={onClose}
        title={title}
        isOnTop={isOnTop}
        disableEntranceAnimation={disableEntranceAnimation}
        footerRenderer={() =>
          canSeeActionButtons ? (
            <Buttons onEdit={handleEdit} onDeleted={handleDelete} source={data.source} />
          ) : undefined
        }
      >
        <BookingDetailContent
          booking={currentSiblingBooking}
          profile={profile}
          loading={isInitialBookingLoading}
          goToDate={goToDate}
          checkCanGoToDate={checkCanGoToDate}
          source={data.source}
        />
      </SidePanel>
    </BookingActivityContextProvider>
  );
};

export const BookingDetailSidePanel = React.memo((props: DetailsSidePanelProps) => {
  if (!props.setActiveBookingIndex) {
    throw new Error("BookingDetailsSidePanel requires setActiveBookingIndex!");
  }

  return (
    <BookingDetailSidePanelContextProvider
      enabled={props.show}
      {...props}
      activeBookingIndex={props.activeBookingIndex}
      setActiveBookingIndex={props.setActiveBookingIndex}
    >
      <BookingDetailSidePanelContent {...props} />
    </BookingDetailSidePanelContextProvider>
  );
});

BookingDetailSidePanel.displayName = "BookingDetailSidePanel";
